/**
 * MBA Lab — pure state model for the Leadership Scenario Simulator. No DOM,
 * storage, or analytics dependencies - leadership-scenario-simulator.js owns
 * all of that and consumes SCENARIOS/TREES/getDebrief() from here.
 *
 * Two scenario kinds share this one tool. The six flat SCENARIOS (setup,
 * three choices, one debrief) and every choice/debrief on them are original
 * analytical applications written for this tool - never presented as VCU
 * case solutions or as having one "correct" answer. The six deeper TREES
 * (multi-node, some choice nodes and some free-text "write" nodes checked
 * against lib/message-checks.js, several distinct endings) are likewise
 * original scenarios, not ported course content - see simulator-trees.js's
 * own doc comment. Every debrief on both kinds names what a choice
 * prioritizes, what it overlooks, and when a different choice (including
 * directive, non-conversational leadership) fits better - the plan's
 * explicit guardrail against implying every situation resolves through
 * empathy and conversation.
 */

import { SCENARIOS } from './simulator-scenarios.js';
import { TREES } from './simulator-trees.js';
import { runChecks } from './message-checks.js';

export const SCHEMA_NAME = 'mbalab.leadership-scenario-simulator';
export const SCHEMA_VERSION = 2;

export const LIMITS = Object.freeze({
  maxSerializedBytes: 20 * 1024,
  writeResponseMaxLength: 600,
});

const SCENARIOS_BY_ID = new Map(SCENARIOS.map((s) => [s.id, s]));
export const TOTAL_SCENARIOS = SCENARIOS.length;

const TREES_BY_ID = new Map(TREES.map((t) => [t.id, t]));
export const TOTAL_TREES = TREES.length;

const OUTCOMES = Object.freeze(['resolved', 'partial', 'stalled', 'damaged']);

export function createInitialState() {
  return { schema: SCHEMA_NAME, version: SCHEMA_VERSION, savedAt: null, selections: {}, treeProgress: {} };
}

function isValidChoiceId(scenarioId, choiceId) {
  const scenario = SCENARIOS_BY_ID.get(scenarioId);
  return !!scenario && scenario.choices.some((c) => c.id === choiceId);
}

/** A tree's write-node text is sanitized against ITS OWN tree's real node
 * ids (not a global check), same discipline as isValidChoiceId above. */
function sanitizeWriteResponses(tree, rawResponses) {
  const r = rawResponses && typeof rawResponses === 'object' ? rawResponses : {};
  const out = {};
  for (const [nodeId, text] of Object.entries(r)) {
    const node = tree.nodes[nodeId];
    if (node && node.kind === 'write' && typeof text === 'string' && text.trim()) {
      out[nodeId] = text.trim().slice(0, LIMITS.writeResponseMaxLength);
    }
  }
  return out;
}

function sanitizeTreeProgressEntry(tree, raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const writeResponses = sanitizeWriteResponses(tree, r.writeResponses);
  // Validated against THIS tree's own debrief keys, not just the global
  // 4-value OUTCOMES enum - not every tree has all 4 (peer-stuck-on-a-decision
  // has no "damaged" ending). Accepting any of the 4 globally-valid names
  // regardless of whether this specific tree actually has text for it let
  // getTreeDebrief return `summary: undefined`, which rendered as the
  // literal word "undefined" in the debrief screen.
  const completedOutcome = OUTCOMES.includes(r.completedOutcome) && tree.debrief[r.completedOutcome] ? r.completedOutcome : null;
  // A completed entry doesn't need a live currentNodeId (the tree may have
  // been edited since); an in-progress entry's currentNodeId must still be
  // a real node in the CURRENT tree data, or it resets to the start rather
  // than pointing at nothing.
  const currentNodeId = completedOutcome
    ? null
    : tree.nodes[r.currentNodeId]
      ? r.currentNodeId
      : tree.start;
  return {
    currentNodeId,
    writeResponses,
    completedOutcome,
    startedAt: typeof r.startedAt === 'number' ? r.startedAt : null,
    completedAt: typeof r.completedAt === 'number' ? r.completedAt : null,
  };
}

function sanitizeTreeProgress(rawTreeProgress) {
  const r = rawTreeProgress && typeof rawTreeProgress === 'object' ? rawTreeProgress : {};
  const out = {};
  for (const tree of TREES) {
    if (r[tree.id]) out[tree.id] = sanitizeTreeProgressEntry(tree, r[tree.id]);
  }
  return out;
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const rawSelections = r.selections && typeof r.selections === 'object' ? r.selections : {};
  const selections = {};
  for (const scenario of SCENARIOS) {
    const choiceId = rawSelections[scenario.id];
    if (typeof choiceId === 'string' && isValidChoiceId(scenario.id, choiceId)) {
      selections[scenario.id] = choiceId;
    }
  }
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    selections,
    treeProgress: sanitizeTreeProgress(r.treeProgress),
  };
}

const MIGRATIONS = {
  0: (state) => ({ ...state, version: 1 }),
  // v1 -> v2: added the 6 deep TREES alongside the original flat SCENARIOS.
  // `selections` is untouched; `treeProgress` is a new, purely additive
  // field, so a v1 user's existing flat-scenario answers survive unchanged.
  1: (state) => ({ ...state, version: 2, treeProgress: {} }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    // Number.isInteger(NaN) is false, so this also rejects a corrupted NaN
    // version - without this, `NaN > SCHEMA_VERSION` and `MIGRATIONS[NaN]`
    // are both silently false/undefined, so a corrupted version would fall
    // through as if it were already current instead of being flagged.
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

export function selectChoice(state, scenarioId, choiceId) {
  if (!isValidChoiceId(scenarioId, choiceId)) return state;
  return { ...state, selections: { ...state.selections, [scenarioId]: choiceId } };
}

export function countExplored(state) {
  return Object.keys(state.selections).length;
}

// --- Deep (tree) scenarios ------------------------------------------------

function freshTreeProgress(now) {
  return { currentNodeId: null, writeResponses: {}, completedOutcome: null, startedAt: now, completedAt: null };
}

export function getTreeProgress(state, treeId) {
  return state.treeProgress[treeId] ?? null;
}

export function countTreesCompleted(state) {
  return Object.values(state.treeProgress).filter((p) => p.completedOutcome).length;
}

/** The live node object at a tree's current position, or null once
 * complete (use getTreeDebrief for the completed view instead). */
export function getCurrentTreeNode(treeId, progress) {
  const tree = TREES_BY_ID.get(treeId);
  if (!tree || !progress || progress.completedOutcome) return null;
  return tree.nodes[progress.currentNodeId] ?? null;
}

/** null until the tree is complete, then {tree, outcome, summary}. */
export function getTreeDebrief(treeId, progress) {
  const tree = TREES_BY_ID.get(treeId);
  if (!tree || !progress || !progress.completedOutcome) return null;
  return { tree, outcome: progress.completedOutcome, summary: tree.debrief[progress.completedOutcome] };
}

/** Runs a write node's checks against a draft, without saving anything -
 * lets the UI show live pass/fail feedback as someone types. */
export function checkTreeWriteDraft(treeId, nodeId, text) {
  const tree = TREES_BY_ID.get(treeId);
  const node = tree?.nodes[nodeId];
  if (!node || node.kind !== 'write') return [];
  return runChecks(text, node.checks);
}

function advanceTo(state, treeId, progress, nextNodeId, now) {
  const tree = TREES_BY_ID.get(treeId);
  const nextNode = tree.nodes[nextNodeId];
  const updated =
    nextNode.kind === 'end'
      ? { ...progress, currentNodeId: null, completedOutcome: nextNode.outcome, completedAt: now }
      : { ...progress, currentNodeId: nextNodeId };
  return { ...state, treeProgress: { ...state.treeProgress, [treeId]: updated } };
}

export function startTree(state, treeId, now) {
  const tree = TREES_BY_ID.get(treeId);
  if (!tree) return state;
  const existing = state.treeProgress[treeId];
  if (existing) return state; // already started - use restartTree to reset deliberately
  return {
    ...state,
    treeProgress: { ...state.treeProgress, [treeId]: { ...freshTreeProgress(now), currentNodeId: tree.start } },
  };
}

export function restartTree(state, treeId, now) {
  const tree = TREES_BY_ID.get(treeId);
  if (!tree) return state;
  return {
    ...state,
    treeProgress: { ...state.treeProgress, [treeId]: { ...freshTreeProgress(now), currentNodeId: tree.start } },
  };
}

/** Advances a choice node. No-op (returns state unchanged) if nodeId isn't
 * actually the tree's current position (a stale UI re-submitting an old
 * screen) or optionId doesn't exist on that node - defends the same way
 * isValidChoiceId does for the flat scenarios above. */
export function chooseTreeOption(state, treeId, nodeId, optionId, now) {
  const tree = TREES_BY_ID.get(treeId);
  const progress = state.treeProgress[treeId];
  if (!tree || !progress || progress.currentNodeId !== nodeId) return state;
  const node = tree.nodes[nodeId];
  if (!node || node.kind !== 'choice') return state;
  const option = node.options.find((o) => o.id === optionId);
  if (!option) return state;
  return advanceTo(state, treeId, progress, option.next, now);
}

/** Advances a write node. Rejects a blank submission (no-op) so a node can
 * never be skipped with empty text - matching logDailyEntry's own rule in
 * journal-model.js. Saves the trimmed, length-capped text either way it's
 * later re-rendered (the debrief screen can show what someone wrote). */
export function submitTreeWrite(state, treeId, nodeId, text, now) {
  const tree = TREES_BY_ID.get(treeId);
  const progress = state.treeProgress[treeId];
  if (!tree || !progress || progress.currentNodeId !== nodeId) return state;
  const node = tree.nodes[nodeId];
  if (!node || node.kind !== 'write') return state;
  const clamped = typeof text === 'string' ? text.trim().slice(0, LIMITS.writeResponseMaxLength) : '';
  if (!clamped) return state;
  const withResponse = { ...progress, writeResponses: { ...progress.writeResponses, [nodeId]: clamped } };
  return advanceTo(state, treeId, withResponse, node.next, now);
}

/** @returns {null | {scenario: object, choice: object}} null if this scenario has no selection yet. */
export function getDebrief(state, scenarioId) {
  const scenario = SCENARIOS_BY_ID.get(scenarioId);
  if (!scenario) return null;
  const choiceId = state.selections[scenarioId];
  if (!choiceId) return null;
  const choice = scenario.choices.find((c) => c.id === choiceId);
  if (!choice) return null;
  return { scenario, choice };
}
